import { generateThumbnailFromUrl } from '../utils';
/**
 * useProjectState.ts
 * 
 * Manages project data, sidebar state, folder organization, and auto-save logic.
 * Extracted from App.tsx to decouple project management from the main UI.
 */

import { useState, useEffect, useRef, useCallback, useMemo, MutableRefObject } from 'react';
import {
    SavedProject,
    Clip,
    Track,
    MontageVersion,
    ChatMessage,
    EditingSuggestion,
    ExportedVideo
} from '../types';
import {
    Folder,
    Project as SidebarProject
} from '../components/SidebarProjectList';
import {
    saveProjectToFirestore,
    getProjectsFromFirestore,
    loadUserSettings,
    saveUserSettings
} from '../firestore';
import firebase from 'firebase/compat/app';

const PROJECT_AUTOSAVE_DEBOUNCE_MS = 60000;

interface UseProjectStateProps {
    user: firebase.User | null;
    allClips: Clip[];
    tracks: Track[];
    montageVersions: MontageVersion[];
    messages: ChatMessage[];
    editingSuggestions: EditingSuggestion[];
    exportedVideosRef: MutableRefObject<ExportedVideo[]>;
}

export const useProjectState = ({
    user,
    allClips,
    tracks,
    montageVersions,
    messages,
    editingSuggestions,
    exportedVideosRef
}: UseProjectStateProps) => {

    // Project Selection State
    const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
    const currentProjectIdRef = useRef<string | null>(null);

    // Sync ref with state for callbacks
    useEffect(() => {
        currentProjectIdRef.current = currentProjectId;
    }, [currentProjectId]);

    // Data State
    const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [rootProjects, setRootProjects] = useState<SidebarProject[]>([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);

    // Guard refs to prevent saving empty folders during load race conditions
    const loadedFolderCountRef = useRef<number>(0); // How many folders were loaded from Firebase
    const isFirstSaveAfterLoadRef = useRef(true); // Skip the first auto-save triggered by isSettingsLoaded transition
    const deletedProjectIdsRef = useRef<Set<string>>(new Set()); // Skip auto-save for recently deleted projects
    const lastAutosavedSignatureRef = useRef<string>('');

    // Callback to mark a project as deleted (prevents auto-save race)
    const markProjectDeleted = useCallback((projectId: string) => {
        deletedProjectIdsRef.current.add(projectId);
        // Auto-cleanup after 10s (well past any debounce window)
        setTimeout(() => deletedProjectIdsRef.current.delete(projectId), 10000);
    }, []);

    // ============================================================================
    // LOAD PROJECTS & USER SETTINGS
    // ============================================================================

    useEffect(() => {
        if (user) {
            Promise.all([
                getProjectsFromFirestore(),
                loadUserSettings()
            ]).then(([projects, settings]) => {
                setSavedProjects(projects);

                // Helper: Convert SavedProject to SidebarProject
                const toSidebarProject = (p: SavedProject): SidebarProject => ({
                    id: p.id,
                    title: p.name,
                    assets: [],
                    updated: new Date(p.updatedAt).toLocaleDateString(),
                    thumbnailUrl: p.thumbnailUrl,
                });

                if (settings) {
                    // 1. Map all available projects for easy lookup
                    const projectMap = new Map(projects.map(p => [p.id, p]));
                    const trackedProjectIds = new Set<string>();

                    // 2. Hydrate Folders
                    const hydratedFolders: Folder[] = settings.folders.map(f => {
                        const folderProjects = f.projectIds
                            .map(pid => {
                                trackedProjectIds.add(pid);
                                return projectMap.get(pid);
                            })
                            .filter((p): p is SavedProject => !!p)
                            .map(toSidebarProject);

                        return {
                            id: f.id,
                            name: f.name,
                            open: f.open,
                            projects: folderProjects,
                            preset: f.preset
                        };
                    });
                    loadedFolderCountRef.current = hydratedFolders.length;
                    setFolders(hydratedFolders);

                    // 3. Find root projects (those not in any folder)
                    const rootProjs = projects
                        .filter(p => !trackedProjectIds.has(p.id))
                        .map(toSidebarProject);

                    setRootProjects(rootProjs);
                } else {
                    // No settings found, put everything in root
                    setRootProjects(projects.map(toSidebarProject));
                }

                // DATA LOADED - Enable Auto-Save
                setIsSettingsLoaded(true);

            }).catch(err => console.error("Failed to load projects:", err));
        } else {
            // Clear state on logout
            setSavedProjects([]);
            setFolders([]);
            setRootProjects([]);
            setIsSettingsLoaded(false);
        }
    }, [user]);

    // ============================================================================
    // AUTO-SAVE LOGIC
    // ============================================================================

    useEffect(() => {
        if (!currentProjectId || !user) return;

        const timer = setTimeout(() => {
            // GUARD: Skip auto-save for recently deleted projects
            if (deletedProjectIdsRef.current.has(currentProjectId)) {
                console.log(`[AutoSave] ⏭️ Skipping save for deleted project ${currentProjectId}`);
                return;
            }

            // Find current project structure to get name/thumbnail
            const project = savedProjects.find(p => p.id === currentProjectId);
            if (project) {
                // Guard against destructive autosave while video uploads are still unresolved.
                const hasUnresolvedVideoBlob = (clips: any[]): boolean => {
                    return clips.some((clip: any) => {
                        const hasBlobVideo = clip?.type === 'video' && typeof clip?.url === 'string' && clip.url.startsWith('blob:');
                        const hasPersistentOriginal = Array.isArray(clip?.audioSources) &&
                            clip.audioSources.some((s: any) => s?.type === 'original' && typeof s?.url === 'string' && !s.url.startsWith('blob:'));
                        if (hasBlobVideo && !hasPersistentOriginal) return true;
                        if (clip?.syntheData?.clips) return hasUnresolvedVideoBlob(clip.syntheData.clips);
                        return false;
                    });
                };

                if (hasUnresolvedVideoBlob(allClips)) {
                    console.log(`[AutoSave] ⏭️ Skipping save: unresolved video blob URLs detected for project ${currentProjectId}`);
                    return;
                }

                if (montageVersions.length === 0 && project.montageVersions?.length > 0) {
                    console.warn(`[AutoSave] ⚠️ WARNING: Overwriting ${project.montageVersions.length} existing montages with 0!`);
                } else {
                    console.log(`[AutoSave] Saving project ${project.name} with ${montageVersions.length} montages.`);
                }

                const updatedProject: SavedProject = {
                    ...project,
                    clips: allClips,
                    tracks: tracks,
                    montageVersions: montageVersions,
                    messages: messages.filter(m => !m.isLoading), // Persist chat history, but skip active loading states (prevents missing onCancel closures on reload)
                    aiSuggestions: editingSuggestions, // Persist AI suggestions
                    // exports: exportedVideos.map(v => ({ id: v.id, name: v.name, urlMP4: v.urlMP4, urlWebM: v.urlWebM, thumbnailUrl: v.thumbnailUrl })), // Persist exports
                    // NOTE: Careful with exports mapping, we need to match the type expected by SavedProject
                    exports: exportedVideosRef.current.map(v => ({
                        id: v.id,
                        name: v.name,
                        urlMP4: v.urlMP4,
                        urlWebM: v.urlWebM,
                        thumbnailUrl: v.thumbnailUrl
                    })),
                    updatedAt: Date.now()
                };

                const autosaveSignature = JSON.stringify({
                    clips: updatedProject.clips,
                    tracks: updatedProject.tracks,
                    montageVersions: updatedProject.montageVersions,
                    messages: updatedProject.messages,
                    aiSuggestions: updatedProject.aiSuggestions,
                    exports: updatedProject.exports
                });

                if (autosaveSignature === lastAutosavedSignatureRef.current) {
                    console.log(`[AutoSave] ⏭️ Skipping unchanged project ${project.name}`);
                    return;
                }

                saveProjectToFirestore(updatedProject).catch(err =>
                    console.error('[AutoSave] Firestore sync failed:', err)
                );
                lastAutosavedSignatureRef.current = autosaveSignature;

                // Update local state to keep it in sync
                setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? updatedProject : p));
            }
        }, PROJECT_AUTOSAVE_DEBOUNCE_MS); // Debounced autosave (no aggressive 2s cycle)

        return () => clearTimeout(timer);
    }, [allClips, tracks, montageVersions, messages, editingSuggestions, currentProjectId, user]);

    // ============================================================================
    // AUTO-HEAL THUMBNAIL LOGIC
    // ============================================================================

    useEffect(() => {
        if (currentProjectId) {
            const healThumbnail = async () => {
                const project = savedProjects.find(p => p.id === currentProjectId);
                const isBroken = project?.thumbnailUrl?.startsWith('blob:');
                const isMissing = !project?.thumbnailUrl;

                if (project && (isBroken || isMissing)) {
                    // Find first clip with a thumbnail
                    const thumbProvider = allClips.find(c => c.thumbnails && c.thumbnails.length > 0);
                    if (thumbProvider && thumbProvider.thumbnails?.[0]) {
                        const newThumb = thumbProvider.thumbnails[0];

                        // Update both lists
                        setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, thumbnailUrl: newThumb } : p));
                        setRootProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, thumbnailUrl: newThumb } : p));
                    } else {
                        // Fallback to first visual clip URL if it's an image or a permanent video URL
                        // For videos, we can use BLOB urls because we generate a base64 thumbnail from them.
                        // For images, we must avoid BLOB urls because we simple copy the URL, and blob URLs expire/are sanitized.
                        const visualClip = allClips.find(c => {
                            if (!c.url || c.url.startsWith('file-requirement:')) return false;
                            if (c.type === 'video') return true; // Video blobs are OK (we calc base64)
                            if (c.type === 'image') return !c.url.startsWith('blob:'); // Image blobs are NOT OK (we just copy URL)
                            return false;
                        });

                        if (visualClip?.url) {
                            let newThumb = visualClip.url;

                            if (visualClip.type === 'video') {
                                try {
                                    console.log("[AutoHeal] Generating thumbnail from", visualClip.url);
                                    newThumb = await generateThumbnailFromUrl(visualClip.url);
                                    console.log("[AutoHeal] Generated:", newThumb ? (newThumb.substring(0, 30) + '...') : 'FAIL');
                                } catch (err) {
                                    console.warn("[AutoHeal] Failed to generate thumbnail from video:", err);
                                    return; // Don't update if failed
                                }
                            }

                            setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, thumbnailUrl: newThumb } : p));
                            setRootProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, thumbnailUrl: newThumb } : p));
                        }
                    }
                }
            };
            healThumbnail(); // Run once
        }
    }, [allClips, currentProjectId]);

    // ============================================================================
    // AUTO-SAVE USER SETTINGS (Folders, Layout)
    // ============================================================================

    useEffect(() => {
        if (!user || !isSettingsLoaded) return;

        // Skip the very first trigger after load — that's just the initial data arriving,
        // not a user-driven change.
        if (isFirstSaveAfterLoadRef.current) {
            isFirstSaveAfterLoadRef.current = false;
            console.log('[useProjectState] Skipping first auto-save (initial data load)');
            return;
        }

        console.log('[useProjectState] User settings changed, queueing save...', { folders: folders.length, roots: rootProjects.length });
        const timer = setTimeout(async () => {
            try {
                // CRITICAL GUARD: Never save empty folders if we loaded folders from Firebase
                // AND we haven't done a successful user-initiated save yet.
                // After the first successful save, the guard is disabled (user can delete all folders).
                if (folders.length === 0 && loadedFolderCountRef.current > 0) {
                    console.warn('[useProjectState] ⚠️ BLOCKED: Refusing to save empty folders (had', loadedFolderCountRef.current, 'loaded). This is likely a race condition.');
                    return;
                }

                console.log('[useProjectState] Saving user settings (Folders/Roots)...', { folders: folders.length, roots: rootProjects.length });
                await saveUserSettings({
                    folders: folders,
                    rootProjectIds: rootProjects.map(p => p.id)
                });
                // After successful save, update the ref. 
                // If user deletes folders one by one, this tracks correctly.
                loadedFolderCountRef.current = folders.length;
            } catch (err) {
                console.error('[useProjectState] Failed to auto-save user settings:', err);
            }
        }, 1000); // 1s debounce to avoid spamming Firestore on drag-ops

        return () => clearTimeout(timer);
    }, [folders, rootProjects, user, isSettingsLoaded]);

    return {
        currentProjectId,
        setCurrentProjectId,
        currentProjectIdRef,
        savedProjects,
        setSavedProjects,
        folders,
        setFolders,
        rootProjects,
        setRootProjects,
        sidebarOpen,
        setSidebarOpen,
        markProjectDeleted
    };
};
